// Mock data for Predrag Petrovic — AI SEO / GEO / AEO Expert Portfolio
// Nav links and social handles are sourced from the centralized site-config.json
// (single source of truth, also consumed by /app/scripts/sync-static-partials.js).
import siteConfig from "../config/site-config.json";

export const profile = {
  name: "Predrag Petrović",
  shortName: "Predrag P.",
  role: "AI SEO Expert",
  specialties: ["GEO", "AEO", "Technical SEO"],
  tagline: "Helping brands stay visible in the age of AI search.",
  description:
    "I engineer visibility strategies for the new search frontier — where ChatGPT, Perplexity, Gemini and Google AI Overviews decide who gets discovered. From classical SEO to Generative & Answer Engine Optimization.",
  location: "Belgrade, Serbia · Working globally",
  email: "design4um@gmail.com",
  phone: "+381 64 000 0000",
  availability: "Available for Q3 / Q4 2026 engagements",
  website: "https://www.vidadeseo.com",
  socials: siteConfig.socials,
  headshot: "/predrag-portrait.jpg",
  altPortrait: "/predrag.jpg"
};

export const stats = [
  { value: "18+", label: "Years in search" },
  { value: "180+", label: "Brands optimized" },
  { value: "47M", label: "AI impressions earned" },
  { value: "94%", label: "Citation accuracy" }
];

export const credentials = [
  "Former Head of Organic Growth, EMEA SaaS",
  "Advisor to 3 YC-backed AI startups"
];

export const services = [
  {
    id: "ai-seo",
    code: "01",
    title: "AI SEO Strategy",
    short: "Foundation for the new search era.",
    description:
      "A modern SEO program that fuses classical ranking signals with entity, semantic, and AI-readability layers. Designed so your brand is found by both Google and Large Language Models.",
    deliverables: [
      "Entity & topical authority audit",
      "LLM-readable content architecture",
      "Schema, embeddings & vector readiness",
      "Quarterly visibility roadmap"
    ],
    icon: "Sparkles"
  },
  {
    id: "geo",
    code: "02",
    title: "Generative Engine Optimization",
    short: "Be cited by ChatGPT, Perplexity & Gemini.",
    description:
      "GEO focuses on becoming a trusted source inside generative answers. We engineer the signals — citations, freshness, structure, retrievability — that convince LLMs to surface your brand.",
    deliverables: [
      "Generative visibility benchmark",
      "Source-worthy content frameworks",
      "Citation & mention engineering",
      "LLM crawl & retrieval optimization"
    ],
    icon: "BrainCircuit"
  },
  {
    id: "aeo",
    code: "03",
    title: "Answer Engine Optimization",
    short: "Win the zero-click conversation.",
    description:
      "AEO turns your content into the definitive answer for high-intent questions across AI Overviews, voice, and featured snippets — capturing demand before it ever reaches a click.",
    deliverables: [
      "Question intent mapping",
      "Answer block engineering",
      "FAQ, HowTo & QAPage schema",
      "AI Overview share tracking"
    ],
    icon: "MessageSquareQuote"
  }
];

export const caseStudies = [
  {
    id: "cs-1",
    client: "Northwind Fintech",
    sector: "B2B SaaS · Fintech",
    headline: "From invisible to industry-cited in 6 months.",
    metric: "+247%",
    metricLabel: "AI citation share",
    summary:
      "Restructured 1,200+ pages around entity clusters and shipped a citation-first content engine. Northwind became the most-quoted source in ChatGPT for 4 of its top 5 commercial queries.",
    tags: ["GEO", "Content", "Schema"],
    image:
      "https://images.unsplash.com/photo-1666875753105-c63a6f3bdc86?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxkYXRhJTIwdmlzdWFsaXphdGlvbnxlbnwwfHx8fDE3Nzc5NjMxODJ8MA&ixlib=rb-4.1.0&q=85&w=1200"
  },
  {
    id: "cs-2",
    client: "Atelier Maison",
    sector: "DTC · Luxury Home",
    headline: "Owning the AI Overview for a $1.4B category.",
    metric: "61%",
    metricLabel: "AI Overview share",
    summary:
      "Built an answer-first content system mapped to 380 commercial questions. Atelier now appears in Google AI Overviews for 61% of tracked queries — a 9× lift over baseline.",
    tags: ["AEO", "Schema", "E-commerce"],
    image:
      "https://images.unsplash.com/photo-1748609160056-7b95f30041f0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxhbmFseXRpY3MlMjB3b3Jrc3BhY2V8ZW58MHx8fHwxNzc4MDY3OTYzfDA&ixlib=rb-4.1.0&q=85&w=1200"
  },
  {
    id: "cs-3",
    client: "Helix Health",
    sector: "HealthTech · YC",
    headline: "Trusted source status across 4 LLMs.",
    metric: "4×",
    metricLabel: "Brand mentions in LLMs",
    summary:
      "A medically-reviewed content moat plus authoritative citation engineering moved Helix from absent to recommended across ChatGPT, Perplexity, Gemini and Claude in two quarters.",
    tags: ["GEO", "E-E-A-T", "YMYL"],
    image:
      "https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHxkYXRhJTIwdmlzdWFsaXphdGlvbnxlbnwwfHx8fDE3Nzc5NjMxODJ8MA&ixlib=rb-4.1.0&q=85&w=1200"
  },
  {
    id: "cs-4",
    client: "Lumen Labs",
    sector: "Developer Tools",
    headline: "Doubled qualified pipeline from organic.",
    metric: "2.1×",
    metricLabel: "Pipeline from organic",
    summary:
      "Refactored docs and use-case pages for LLM retrieval. Lumen now ships as a top-3 recommendation when developers ask AI assistants for tooling — translating to a 2.1× pipeline lift.",
    tags: ["AI SEO", "DevRel", "Docs"],
    image:
      "https://images.unsplash.com/photo-1723987251277-18fc0a1effd0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwzfHxhbmFseXRpY3MlMjB3b3Jrc3BhY2V8ZW58MHx8fHwxNzc4MDY3OTYzfDA&ixlib=rb-4.1.0&q=85&w=1200"
  }
];

export const process = [
  {
    step: "01",
    title: "Discovery & Audit",
    detail:
      "Deep dive into your current visibility across Google, AI Overviews and LLMs. We benchmark citations, entities, retrievability and competitive moats."
  },
  {
    step: "02",
    title: "Strategy & Roadmap",
    detail:
      "A prioritized 90-day plan mapping topical territory, answer surfaces and the content + technical work required to own them."
  },
  {
    step: "03",
    title: "Implementation",
    detail:
      "We ship — answer blocks, schema, internal linking, entity pages, citation outreach, freshness loops. Your team or mine, your pace."
  },
  {
    step: "04",
    title: "Measurement & Iteration",
    detail:
      "Custom dashboards track AI Overview share, LLM citations, branded prompts and downstream pipeline. We iterate weekly, not quarterly."
  },
  {
    step: "05",
    title: "Scale & Compound",
    detail:
      "Once the engine is live, we expand into new languages, intents and surfaces — turning visibility into a durable, compounding moat."
  }
];

export const testimonials = [
  {
    id: "t1",
    quote:
      "Predrag is the rare strategist who understands both classical SEO and what's actually happening inside LLMs. He rebuilt our discovery in six months.",
    name: "Antonije Pušić",
    nameUrl: "https://ramboamadeus.com/",
    title: "Svetski Kilo Tzar",
    company: ""
  },
  {
    id: "t2",
    quote:
      "We hired three agencies before Predrag. None of them were even talking about GEO. He's not a step ahead — he's a category ahead.",
    name: "Marko Pejović",
    nameUrl: "https://www.extreme-prohosting.com/",
    title: "CEO",
    company: "Extreme proHosting"
  },
  {
    id: "t3",
    quote:
      "Calm, surgical, deeply technical. Predrag turned our docs into the answer engineers get when they ask Claude or ChatGPT.",
    name: "Yuki Tanaka",
    title: "Founder",
    company: "Lumen Labs"
  },
  {
    id: "t4",
    quote:
      "Working with Predrag felt like adding a fractional Head of AI Search. The frameworks alone were worth the engagement.",
    name: "Sofia Marín",
    title: "Head of Brand",
    company: "Helix Health"
  },
  {
    id: "t5",
    quote:
      "Predrag is a true expert when it comes to digital marketing. He is creative, responsive and passionate about his work. His strategy helps us to get closer to our customers! I highly recommend Predrag to anyone who needs digital marketing.",
    name: "Aleksandar Kostić",
    title: "Award-Winning Film Director, Cinematographer & Producer",
    company: "Kostic Films LLC"
  }
];

export const insights = [
  {
    id: "p1",
    title: "GEO vs SEO: what actually changes when LLMs become the front door",
    excerpt:
      "The mental model shift most teams miss — and the four signal categories you should rebuild your strategy around.",
    category: "Generative SEO",
    readTime: "8 min read",
    date: "Jul 02, 2026"
  },
  {
    id: "p2",
    title: "Engineering citations: how to become the source ChatGPT quotes",
    excerpt:
      "A repeatable framework for citation-worthy content, drawn from 18 months of GEO engagements across SaaS and DTC.",
    category: "GEO Playbook",
    readTime: "11 min read",
    date: "Jun 18, 2026"
  },
  {
    id: "p3",
    title: "AEO in 2026: winning AI Overviews without losing your brand voice",
    excerpt:
      "Answer-engine optimization doesn't mean writing like a robot. Here's how to stay distinctive while still getting picked.",
    category: "Answer Engines",
    readTime: "6 min read",
    date: "May 29, 2026"
  }
];

export const clientLogos = [
  "NORTHWIND",
  "ATELIER MAISON",
  "HELIX HEALTH",
  "LUMEN LABS",
  "VANTA·IO",
  "FORMA",
  "BRIGHTLINE"
];

// Static HTML pages use absolute paths like "/#about"; inside the React SPA
// we keep them as in-page anchors ("#about") for smooth-scroll behaviour.
export const navLinks = siteConfig.nav.en.map(({ label, href }) => ({
  label,
  href: href.startsWith("/#") ? href.slice(1) : href,
}));
